import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from "@angular/core";
import { Tutorial, TutorialService, TutorialStep } from "@core/services/tutorial.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { combineLatest, fromEvent, Subscription } from "rxjs";
import { auditTime, filter, take, takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-tutorial-tooltip",
  template: `
    <div *ngIf="step" [ngClass]="[step.placement || 'bottom']" [ngStyle]="style" class="tutorial-tooltip">
      <div [ngStyle]="arrowStyle" class="tooltip-arrow"></div>
      <div class="tooltip-content">
        <div class="tooltip-text">{{ step.content }}</div>
        <div class="tooltip-actions">
          <button *ngIf="enableNotNow" (click)="notNow()" class="btn btn-sm btn-link mb-0 text-start">
            {{ "Not now" | translate }}
          </button>
          <button
            *ngIf="showSkipButton && !isOnlyStep"
            (click)="skipTutorial()"
            class="btn btn-sm btn-link mb-0 text-start"
          >
            {{ "Skip tutorial" | translate }}
          </button>
          <button (click)="nextStep()" class="btn btn-sm btn-primary">
            {{ isLastStep ? ("Got it" | translate) : ("Next" | translate) }}
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ["./tutorial-tooltip.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TutorialTooltipComponent implements OnInit, OnDestroy {
  public enableNotNow = false;
  public step: TutorialStep | null = null;
  public style: { [key: string]: string } = {};
  public arrowStyle: { [key: string]: string } = {};
  public isLastStep = false;
  public isOnlyStep = false;
  public showSkipButton = true;
  private subscription = new Subscription();
  private resizeSubscription = new Subscription();
  private targetElement: HTMLElement | null = null;
  private currentTutorial: Tutorial | null = null;

  private readonly isBrowser: boolean;

  constructor(
    private readonly tutorialService: TutorialService,
    private readonly elementRef: ElementRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly utilsService: UtilsService,
    private readonly ngZone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    // Ensure any tooltip elements start completely hidden
    this.utilsService
      .delay(0)
      .pipe(take(1))
      .subscribe(() => {
        const tooltipElement = this.elementRef.nativeElement.querySelector(".tutorial-tooltip") as HTMLElement;
        if (tooltipElement) {
          tooltipElement.style.visibility = "hidden";
          tooltipElement.style.opacity = "0";
        }

        // Remove tutorial-active class from host element initially
        this.elementRef.nativeElement.classList.remove("tutorial-active");
      });

    this.subscription.add(
      combineLatest([this.tutorialService.activeTutorial$, this.tutorialService.currentStepIndex$]).subscribe(
        ([tutorial, index]) => {
          if (!this.isBrowser) {
            return;
          }

          const tutorialId = tutorial ? tutorial.id : "none";
          const stepSelector =
            tutorial && index >= 0 && index < tutorial.steps.length ? tutorial.steps[index].selector : "none";

          if (tutorial && index >= 0 && index < tutorial.steps.length) {
            // Hide tooltip immediately when state changes
            const tooltipElement = this.elementRef.nativeElement.querySelector(".tutorial-tooltip") as HTMLElement;
            if (tooltipElement) {
              // Apply fade-out animation when changing steps
              tooltipElement.style.animation = "fadeOut 0.2s ease-out forwards";
              tooltipElement.style.opacity = "0";
              tooltipElement.style.visibility = "hidden";
              tooltipElement.classList.remove("visible");
            }

            // Add tutorial-active class to host for backdrop
            this.elementRef.nativeElement.classList.add("tutorial-active");

            this.currentTutorial = tutorial;
            this.step = tutorial.steps[index];
            this.isLastStep = index === tutorial.steps.length - 1;
            this.isOnlyStep = tutorial.steps.length === 1;
            this.showSkipButton = !tutorial.unskippable; // show skip button unless tutorial is unskippable
            this.enableNotNow = tutorial.enableNotNow || false; // show "Not now" button if specified

            // Position tooltip after a delay to ensure target element is rendered and previous tooltip has faded out
            this.utilsService
              .delay(100) // Minimal delay to ensure fade-out completes
              .pipe(take(1))
              .subscribe(() => {
                this._positionTooltip();

                // Setup resize handler to reposition tooltip when window size changes
                this._setupResizeHandler();

                // If element not found, try again after a longer delay
                // Note: _positionTooltip now handles missing elements by skipping to the next step
                if (!this.targetElement) {
                  this.utilsService
                    .delay(1000)
                    .pipe(take(1))
                    .subscribe(() => {
                      this._positionTooltip();
                      // Setup resize handler again if we find the element on retry
                      if (this.targetElement) {
                        this._setupResizeHandler();
                      }
                      this.changeDetectorRef.markForCheck();
                    });
                }

                this.changeDetectorRef.markForCheck();
              });

            this.changeDetectorRef.markForCheck();
          } else {
            this.step = null;

            this._hideTooltip();

            // Cleanup resize handler when tutorial is not active
            this.resizeSubscription.unsubscribe();
            this.resizeSubscription = new Subscription();

            this.changeDetectorRef.markForCheck();
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    // Clean up any applied styles
    this._resetTargetElementStyles();

    // Hide tooltip and ensure host element doesn't block pointer events
    this._hideTooltip();

    this.subscription.unsubscribe();
    this.resizeSubscription.unsubscribe();
  }

  private _setupResizeHandler(): void {
    // Unsubscribe from any existing resize subscriptions
    this.resizeSubscription.unsubscribe();
    this.resizeSubscription = new Subscription();

    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      this.resizeSubscription.add(
        fromEvent(window, "resize")
          .pipe(
            // Wait until resize events slow down (300ms)
            auditTime(300),
            // Only process if we have an active step and target
            filter(() => !!this.step && !!this.targetElement),
            // Run until component is destroyed
            takeUntil(this.tutorialService.activeTutorial$.pipe(filter(tutorial => !tutorial)))
          )
          .subscribe(() => {
            // Run positioning inside Angular zone to trigger change detection
            this.ngZone.run(() => {
              this._positionTooltip();
            });
          })
      );
    });
  }

  nextStep(): void {
    this._resetTargetElementStyles();

    const tooltipElement = this.elementRef.nativeElement.querySelector(".tutorial-tooltip") as HTMLElement;
    if (tooltipElement) {
      tooltipElement.style.animation = "fadeOut 0.15s ease-out forwards";
      tooltipElement.style.opacity = "0";

      this.utilsService.delay(150).subscribe(() => {
        tooltipElement.style.visibility = "hidden";
        this.tutorialService.nextStep();
      });
    } else {
      this.tutorialService.nextStep();
    }
  }

  skipTutorial(): void {
    this._resetTargetElementStyles();
    this._hideTooltip();
    this.tutorialService.endTutorial(true);
  }

  notNow(): void {
    this._resetTargetElementStyles();
    this._hideTooltip();
    this.tutorialService.suspendAllTutorials();
    this.tutorialService.endTutorial(false);
  }

  private _hideTooltip(): void {
    // Hide tooltip when no steps
    const tooltipElement = this.elementRef.nativeElement.querySelector(".tutorial-tooltip") as HTMLElement;
    if (tooltipElement) {
      tooltipElement.style.visibility = "hidden";
      tooltipElement.style.opacity = "0";
      tooltipElement.style.pointerEvents = "none";
      tooltipElement.classList.remove("visible");
    }

    // Remove tutorial-active class from host to hide backdrop and disable pointer events
    this.elementRef.nativeElement.classList.remove("tutorial-active");
  }

  private _resetTargetElementStyles(): void {
    if (this.targetElement) {
      this.targetElement.style.removeProperty("z-index");
      this.targetElement.style.removeProperty("pointer-events");
      this.targetElement.style.removeProperty("box-shadow");
      this.targetElement.style.removeProperty("transition");
    }
  }

  private _positionTooltipCentered(): void {
    if (!this.isBrowser || !this.step) {
      return;
    }

    // Calculate centered position
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Estimate tooltip dimensions
    const tooltipWidth = Math.min(
      window.innerWidth < 376 ? 220 : window.innerWidth < 768 ? 250 : 300,
      window.innerWidth * 0.9
    );
    const tooltipHeight = 150; // Estimated height

    // Center the tooltip
    const left = (windowWidth - tooltipWidth) / 2;
    const top = (windowHeight - tooltipHeight) / 2;

    this.style = {
      top: `${top}px`,
      left: `${left}px`
    };

    // Hide the arrow for centered tooltips
    this.arrowStyle = {
      display: "none"
    };

    // Make the tooltip visible
    requestAnimationFrame(() => {
      const tooltipElement = this.elementRef.nativeElement.querySelector(".tutorial-tooltip") as HTMLElement;
      if (tooltipElement) {
        // Force layout recalculation before showing
        void tooltipElement.offsetHeight;

        // Apply animation and make visible
        tooltipElement.style.animation = "fadeIn 0.15s ease-out forwards";
        tooltipElement.style.visibility = "visible";
        tooltipElement.style.opacity = "1";
        tooltipElement.classList.add("visible");
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  private _positionTooltip(): void {
    if (!this.isBrowser || !this.step) {
      return;
    }

    // Find the target element
    this.targetElement = this.document.querySelector(this.step.selector) as HTMLElement;

    // If the target element doesn't exist, show the tooltip centered on screen
    if (!this.targetElement) {
      this._positionTooltipCentered();
      return;
    }

    // Check if element is visible (not hidden by CSS or media queries)
    const style = window.getComputedStyle(this.targetElement);
    const rect = this.targetElement.getBoundingClientRect();

    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0" ||
      rect.width === 0 ||
      rect.height === 0
    ) {
      // Element is hidden, skip to the next step
      this.utilsService.delay(50).subscribe(() => {
        this.nextStep();
      });
      return;
    }

    // Check if element needs scrolling
    const initialRect = this.targetElement.getBoundingClientRect();
    const isInView = initialRect.top >= 0 && initialRect.bottom <= window.innerHeight;

    if (!isInView) {
      // Scroll element into view
      this.targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });

      // Wait for scroll to complete using position tracking
      this._waitForScrollComplete(() => {
        this._positionTooltipAfterScroll();
      });
    } else {
      // Element is already in view, position immediately
      this._positionTooltipAfterScroll();
    }
  }

  private _waitForScrollComplete(callback: () => void): void {
    let lastPos: number | null = null;
    let samePositionCount = 0;

    const checkPosition = () => {
      if (!this.targetElement) {
        callback();
        return;
      }

      const currentPos = this.targetElement.getBoundingClientRect().top;

      if (currentPos === lastPos) {
        samePositionCount++;
        // Consider scroll complete after position unchanged for 3 frames
        if (samePositionCount >= 3) {
          callback();
          return;
        }
      } else {
        samePositionCount = 0;
      }

      lastPos = currentPos;
      requestAnimationFrame(checkPosition);
    };

    requestAnimationFrame(checkPosition);
  }

  private _positionTooltipAfterScroll(): void {
    if (!this.targetElement) {
      return;
    }

    // Use IntersectionObserver to detect when target element is in view
    const intersectionObserver = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          // Element is in view, stop observing
          intersectionObserver.disconnect();

          // Position the tooltip once the element is in view
          // Get updated position after scrolling
          const rect = this.targetElement.getBoundingClientRect();
          const tooltipEl = this.elementRef.nativeElement.querySelector(".tutorial-tooltip");

          if (!tooltipEl) {
            return;
          }

          // Calculate tooltip position based on placement
          const placement = this.step.placement || "bottom";

          // Calculate available space in each direction
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;

          // Determine if we're on mobile
          const isMobile = window.innerWidth < 768;

          // Calculate tooltip dimensions based on screen size
          const tooltipWidth = Math.min(
            window.innerWidth < 376 ? 220 : window.innerWidth < 768 ? 250 : 300,
            window.innerWidth * 0.9
          );
          const tooltipHeight = 150; // Estimated height, will be updated after rendering

          // Use smaller margins on mobile
          const margin = isMobile ? 5 : 15;

          // Check if we have enough space for the default placement
          let finalPlacement = placement;

          // Choose best placement based on available space if not specified
          if (placement === "auto") {
            const spaceBelow = windowHeight - rect.bottom - margin;
            const spaceAbove = rect.top - margin;
            const spaceRight = windowWidth - rect.right - margin;
            const spaceLeft = rect.left - margin;

            const spaces = [
              { placement: "bottom", space: spaceBelow },
              { placement: "top", space: spaceAbove },
              { placement: "right", space: spaceRight },
              { placement: "left", space: spaceLeft }
            ];

            // Sort by available space and pick the best one
            spaces.sort((a, b) => b.space - a.space);
            finalPlacement = spaces[0].placement as "top" | "bottom" | "left" | "right";
          }

          // Calculate ideal position (without adjustments for screen edges)
          let idealLeft = 0;
          let idealTop = 0;

          // Calculate adjusted position with bounds checking
          let adjustedLeft = 0;
          let adjustedTop = 0;

          // Position based on finalized placement
          switch (finalPlacement) {
            case "top":
              idealLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
              idealTop = rect.top - tooltipHeight - margin;

              // On small screens, allow the tooltip to get closer to edges
              if (isMobile && rect.left < tooltipWidth / 2) {
                // Target is near left edge, align tooltip to left
                idealLeft = 0;
              } else if (isMobile && windowWidth - rect.right < tooltipWidth / 2) {
                // Target is near right edge, align tooltip to right
                idealLeft = windowWidth - tooltipWidth;
              }

              adjustedTop = Math.max(0, idealTop);
              adjustedLeft = Math.min(Math.max(0, idealLeft), windowWidth - tooltipWidth);

              this.style = {
                top: `${adjustedTop}px`,
                left: `${adjustedLeft}px`
              };
              break;

            case "bottom":
            default:
              idealLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
              idealTop = rect.bottom + margin;

              // On small screens, allow the tooltip to get closer to edges
              if (isMobile && rect.left < tooltipWidth / 2) {
                // Target is near left edge, align tooltip to left
                idealLeft = 0;
              } else if (isMobile && windowWidth - rect.right < tooltipWidth / 2) {
                // Target is near right edge, align tooltip to right
                idealLeft = windowWidth - tooltipWidth;
              }

              adjustedTop = Math.min(idealTop, windowHeight - tooltipHeight);
              adjustedLeft = Math.min(Math.max(0, idealLeft), windowWidth - tooltipWidth);

              this.style = {
                top: `${adjustedTop}px`,
                left: `${adjustedLeft}px`
              };
              break;

            case "left":
              idealLeft = rect.left - tooltipWidth - margin;
              idealTop = rect.top + rect.height / 2 - tooltipHeight / 2;

              // On small screens, allow the tooltip to get closer to edges
              if (isMobile && rect.top < tooltipHeight / 2) {
                // Target is near top edge, align tooltip to top
                idealTop = 0;
              } else if (isMobile && windowHeight - rect.bottom < tooltipHeight / 2) {
                // Target is near bottom edge, align tooltip to bottom
                idealTop = windowHeight - tooltipHeight;
              }

              adjustedTop = Math.min(Math.max(0, idealTop), windowHeight - tooltipHeight);
              adjustedLeft = Math.max(0, idealLeft);

              this.style = {
                top: `${adjustedTop}px`,
                left: `${adjustedLeft}px`
              };
              break;

            case "right":
              idealLeft = rect.right + margin;
              idealTop = rect.top + rect.height / 2 - tooltipHeight / 2;

              // On small screens, allow the tooltip to get closer to edges
              if (isMobile && rect.top < tooltipHeight / 2) {
                // Target is near top edge, align tooltip to top
                idealTop = 0;
              } else if (isMobile && windowHeight - rect.bottom < tooltipHeight / 2) {
                // Target is near bottom edge, align tooltip to bottom
                idealTop = windowHeight - tooltipHeight;
              }

              adjustedTop = Math.min(Math.max(0, idealTop), windowHeight - tooltipHeight);
              adjustedLeft = Math.min(idealLeft, windowWidth - tooltipWidth);

              this.style = {
                top: `${adjustedTop}px`,
                left: `${adjustedLeft}px`
              };
              break;
          }

          // Calculate how much the tooltip was shifted from ideal position
          const horizontalShift = adjustedLeft - idealLeft;
          const verticalShift = adjustedTop - idealTop;

          // Apply custom arrow position if specified
          if (this.step.arrowPositionTop || this.step.arrowPositionLeft) {
            this.arrowStyle = {
              top: this.step.arrowPositionTop || "",
              left: this.step.arrowPositionLeft || ""
            };
          } else {
            // Calculate automatic arrow position based on placement and shifts
            switch (finalPlacement) {
              case "top":
                // For top placement, arrow is at the bottom pointing down
                // The tooltip arrow is created with borders where only the top border has color

                // Get center of target element in viewport coordinates
                const targetCenterX = rect.left + rect.width / 2;

                // Get left edge of tooltip in viewport coordinates
                const tooltipLeft = parseFloat(this.style.left.replace("px", ""));

                // Calculate the x-position for the arrow relative to the tooltip's left edge
                // This puts the *center* of the arrow directly under the target's center
                const arrowPosition = targetCenterX - tooltipLeft;

                // Ensure the arrow stays within the tooltip bounds
                // Calculate tooltip width accounting for mobile resizing
                const actualTooltipWidth = Math.min(
                  window.innerWidth < 376 ? 220 : window.innerWidth < 768 ? 250 : 300,
                  window.innerWidth * 0.9
                );

                // Clamp the arrow position to keep it within the tooltip
                const clampedPosition = Math.max(
                  20, // Minimum 20px from left edge
                  Math.min(arrowPosition, actualTooltipWidth - 20) // Maximum: tooltip width - 20px
                );

                this.arrowStyle = {
                  bottom: "-10px", // Position at bottom edge of tooltip
                  left: `${clampedPosition}px`,
                  marginLeft: "-10px" // Offset by half the arrow width
                };
                break;

              case "bottom":
                // For bottom placement, arrow is at the top pointing up
                // The tooltip arrow is created with borders where only the bottom border has color

                // Get center of target element in viewport coordinates
                const targetCenterXBottom = rect.left + rect.width / 2;

                // Get left edge of tooltip in viewport coordinates
                const tooltipLeftBottom = parseFloat(this.style.left.replace("px", ""));

                // Calculate the x-position for the arrow relative to the tooltip's left edge
                // This puts the *center* of the arrow directly above the target's center
                const arrowPositionBottom = targetCenterXBottom - tooltipLeftBottom;

                // Ensure the arrow stays within the tooltip bounds for mobile
                // Calculate tooltip width accounting for mobile resizing
                const actualTooltipWidthBottom = Math.min(
                  window.innerWidth < 376 ? 220 : window.innerWidth < 768 ? 250 : 300,
                  window.innerWidth * 0.9
                );

                // Clamp the arrow position to keep it within the tooltip
                const clampedPositionBottom = Math.max(
                  20, // Minimum 20px from left edge
                  Math.min(arrowPositionBottom, actualTooltipWidthBottom - 20) // Maximum: tooltip width - 20px
                );

                this.arrowStyle = {
                  top: "-10px", // Position at top edge of tooltip
                  left: `${clampedPositionBottom}px`,
                  marginLeft: "-10px" // Offset by half the arrow width
                };
                break;

              case "left":
                // For left placement, arrow is at the right pointing right
                // The tooltip arrow is created with borders where only the left border has color

                // Get center of target element in viewport coordinates (vertically)
                const targetCenterYLeft = rect.top + rect.height / 2;

                // Get top edge of tooltip in viewport coordinates
                const tooltipTopLeft = parseFloat(this.style.top.replace("px", ""));

                // Calculate the y-position for the arrow relative to the tooltip's top edge
                // This puts the *center* of the arrow directly pointing to the target's center
                const arrowPositionLeft = targetCenterYLeft - tooltipTopLeft;

                // Ensure the arrow stays within the tooltip bounds
                // Calculate tooltip height (estimated)
                const tooltipHeightLeft = 150; // Estimated height

                // Clamp the arrow position to keep it within the tooltip
                const clampedPositionLeft = Math.max(
                  20, // Minimum 20px from top edge
                  Math.min(arrowPositionLeft, tooltipHeightLeft - 20) // Maximum: tooltip height - 20px
                );

                this.arrowStyle = {
                  right: "-10px", // Position at right edge of tooltip
                  top: `${clampedPositionLeft}px`,
                  marginTop: "-10px" // Offset by half the arrow height
                };
                break;

              case "right":
                // For right placement, arrow is at the left pointing left
                // The tooltip arrow is created with borders where only the right border has color

                // Get center of target element in viewport coordinates (vertically)
                const targetCenterYRight = rect.top + rect.height / 2;

                // Get top edge of tooltip in viewport coordinates
                const tooltipTopRight = parseFloat(this.style.top.replace("px", ""));

                // Calculate the y-position for the arrow relative to the tooltip's top edge
                // This puts the *center* of the arrow directly pointing to the target's center
                const arrowPositionRight = targetCenterYRight - tooltipTopRight;

                // Ensure the arrow stays within the tooltip bounds
                // Calculate tooltip height (estimated)
                const tooltipHeightRight = 150; // Estimated height

                // Clamp the arrow position to keep it within the tooltip
                const clampedPositionRight = Math.max(
                  20, // Minimum 20px from top edge
                  Math.min(arrowPositionRight, tooltipHeightRight - 20) // Maximum: tooltip height - 20px
                );

                this.arrowStyle = {
                  left: "-10px", // Position at left edge of tooltip
                  top: `${clampedPositionRight}px`,
                  marginTop: "-10px" // Offset by half the arrow height
                };
                break;
            }
          }

          // Make the tooltip visible - use requestAnimationFrame for visibility
          requestAnimationFrame(() => {
            const tooltipElement = this.elementRef.nativeElement.querySelector(".tutorial-tooltip") as HTMLElement;
            if (tooltipElement) {
              // Force layout recalculation before showing
              void tooltipElement.offsetHeight;

              // Apply animation and make visible
              tooltipElement.style.animation = "fadeIn 0.15s ease-out forwards";
              tooltipElement.style.visibility = "visible";
              tooltipElement.style.opacity = "1";
              tooltipElement.classList.add("visible");
            }

            this.changeDetectorRef.markForCheck();
          });
        }
      },
      { threshold: 0.1 } // Trigger when at least 10% of the target is visible
    );

    // Start observing the target element
    intersectionObserver.observe(this.targetElement);
  }
}
